import { graphql, HttpResponse, delay } from 'msw';
import {
	healthyDealsCategoryListQuery,
	healthyDealsFilterDealsQuery,
} from '../graphql/queries';

import {
	afdDetails,
	specialtyDetails,
	vendorDetails,
	vendorDetailsFitbitPromoCode,
	vendorDetailsHowToDescriptionFromContentful,
	vendorDetailsPromoCodeRunOut,
	vendorDetailsSinglePromoCode,
} from './fixtures/DealDetailsMock';

// Settings used by the mocks to obtain different scenarios by Storybook and tests
const mockSettingsDefaults = {
	returnError: false,
	returnMessage: '',
	totalCount: 100,
	networkDelay: 500,
	loading: false,
	noImages: false,
	testLoading: false,
};
const mockSettings = { ...mockSettingsDefaults };

/**
 * Updates mock settings by first restoring the defaults and then applying only the
 * requested changes.
 * @param {Object} settings The settings to apply to the mock settings
 * @return {Null}
 */
export function updateMockSettings(settings) {
	Object.keys(mockSettingsDefaults).forEach(key => {
		mockSettings[key] =
			settings[key] === undefined ? mockSettingsDefaults[key] : settings[key];
	});
}

/**
 * Helper function to check if an environment is test or not
 */
const isTestEnv = () => process.env.NODE_ENV === 'test';

/**
 * Setting each deal type for every itertion of i
 */
const setDealType = i =>
	[
		'afd',
		'vendor',
		'vendor',
		'vendor',
		'vendor',
		'vendor',
		'specialty',
		'resourcelibrary',
	][i % 8];

/**
 * Setting discount type for every itertion of i
 */
const setDiscountType = i => ['NORMAL', 'limited'][i % 2];

/**
 * Setting discount text for every itertion of i
 */
const setDealDiscountText = i =>
	[null, '25% off thru 12/25', '50% off thru 12/25'][i % 3];

const mockContentfulImage = {
	name: 'Healthy Deals Card Nutritional Services',
	altText: 'This is an altText',
	imageXs: {
		fileName: 'Card Nutritional Services',
		url: 'https://material-ui.com/static/images/cards/contemplative-reptile.jpg',
		width: 960,
		height: 540,
	},
	imageSm: null,
	imageMd: null,
};

const dynamicDelay = () =>
	isTestEnv() ? mockSettings.networkDelay : 'infinite';

/**
 * Array of mock handlers that service GraphQL requests from this project.
 */
export const handlers = [
	graphql.query(healthyDealsCategoryListQuery, async () => {
		// Simulate the server being down
		if (mockSettings.returnError) {
			return HttpResponse.json(null, { status: 500 });
		}
		if (mockSettings.loading) {
			await delay(dynamicDelay());
			return HttpResponse.json(null);
		}
		return HttpResponse.json({
			data: {
				healthyDealsFilterList: {
					options: [
						{
							id: '1',
							label: 'dance',
						},
						{
							id: '2',
							label: 'yoga',
						},
						{
							id: '3',
							label: 'cycle',
						},
						{
							id: '4',
							label: 'arms',
						},
						{
							id: '5',
							label: 'equipment',
						},
						{
							id: '6',
							label: 'membership',
						},
					],
				},
			},
		});
	}),
	graphql.query(healthyDealsFilterDealsQuery, async ({ variables }) => {
		const { take, skip } = variables;
		const totalCount = 20;
		const setDealName = i =>
			[
				'afd',
				'vendor no promo code',
				'vendor single promo code',
				'vendor fitbit promo code',
				'vendor fitbit How to Section description from contentful',
				'vendor promo code runs out',
				'specialty',
				'resourcelibrary',
			][i % 8];

		// Mock data for ${first} number of items
		const itemsArray = Array.from({ length: take }, (_, i) => ({
			id: ((skip - 1) * skip + i + 1).toString(),
			dealName: `Card ${skip + i} - ${setDealName(i)}`,
			cardTitle: `Card Title ${skip + i} - ${setDealName(i)}`,
			dealType: `${setDealType(i)}`,
			dealDescription:
				'This is card description. Please check out the card description',
			dealDiscount: setDealDiscountText(i),
			dealImage: mockContentfulImage,
			discountBadgeType: setDiscountType(i),
		}));
		// Simulate the server being down
		if (mockSettings.returnError)
			return HttpResponse.json(null, { status: 500 });
		if (mockSettings?.loading) {
			await delay(dynamicDelay());
			return HttpResponse.json(null);
		}
		return HttpResponse.json({
			data: {
				healthyDealsFilterDeals: {
					items: [...itemsArray],
					totalCount,
				},
			},
		});
	}),
	graphql.query(/DealDetail$/, async ({ variables }) => {
		// Simulate the server being down
		if (mockSettings?.returnError) {
			return HttpResponse.json(null, { status: 500 });
		}
		if (mockSettings.loading) {
			await delay(dynamicDelay());
			return HttpResponse.json(null);
		}

		// Mock data for recommended Deals
		const setDealName = i =>
			[
				'vendor no promo code',
				'vendor single promo code',
				'vendor fitbit promo code',
				'specialty',
			][i % 4];

		const itemsArray = Array.from({ length: 4 }, (_, i) => ({
			id: [2, 3, 4, 5][i % 4],
			dealName: `Card ${i} - ${setDealName(i)}`,
			dealType: `${['vendor', 'vendor', 'vendor', 'specialty'][i % 4]}`,
			cardTitle: `Card Title ${i} - ${setDealName(i)}`,
			dealDescription:
				'This is card description. Please check out the card description',
			dealDiscount: setDealDiscountText(i),
			dealImage: mockContentfulImage,
			discountBadgeType: setDiscountType(i),
		}));

		return HttpResponse.json({
			data: {
				healthyDealsDealDetail: (() => {
					const { dealId } = variables;
					switch (dealId % 8) {
						case 1:
							return { ...afdDetails, recommendedDeals: [...itemsArray] };
						case 2:
							return { ...vendorDetails, recommendedDeals: [...itemsArray] };
						case 3:
							return {
								...vendorDetailsSinglePromoCode,
								recommendedDeals: [...itemsArray],
							};
						case 4:
							return {
								...vendorDetailsFitbitPromoCode,
								recommendedDeals: [...itemsArray],
							};
						case 5:
							return {
								...vendorDetailsHowToDescriptionFromContentful,
								recommendedDeals: [...itemsArray],
							};

						case 6:
							return {
								...vendorDetailsPromoCodeRunOut,
								recommendedDeals: [...itemsArray],
							};
						case 7:
							return {
								...specialtyDetails,
								recommendedDeals: [...itemsArray],
							};

						default:
							return null;
					}
				})(),
			},
		});
	}),
];
